import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefLovProps, RCCRefModalProps, RCCServicesApi} from "react-common-components";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import DeleteModal from "src/components/DeleteModal";
import ModalSide from "src/components/Modal/styles";
import Modal from "src/components/Modal";
import Audit from "src/components/Audit";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import LovDimensions from "src/components/Lov/Dimensions";
import Permissions from "src/components/Permissions";
import stringToNumber from "src/utils/stringToNumber";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import Tabs from "src/components/Tabs";
import { useAuth } from "src/context/Auth/context";
import { simplePermissionType } from "src/components/Permissions/interface";
import { ITypeModal } from "src/components/Modal/interface";
import { AddOrChangeSchema, deleteSchema } from "./schemas";
import { IRefSwotCrudModal, ISwot, ISwotPermission } from "../../interface";

const SwotCrudModals: React.ForwardRefRenderFunction<IRefSwotCrudModal> = (_,ref) => {
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();
	const { user } = useAuth();
	const { currentLanguage } = useTranslation();

	const [onCloseFunction,setOnCloseFunction] = useState<((swot?:ISwot|number)=>void )>();
	const [isAdd,setIsAdd] = useState<boolean>(false);
	const [isCopy,setIsCopy] = useState<boolean>(false);
	const [selectedSwot, setSelectedSwot] = useState<ISwot>({} as ISwot);
	const [modalTitle,setModalTitle] = useState<string>("");
	const [deleteSwot,setDeleteSwot] = useState<{id:number,title:string}>();

	// Modal Delete
	const modalDelete = useRef<RCCRefModalProps>(null);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const lovRef = useRef<RCCRefLovProps>(null);

	const { 
		handleSubmit: handleSubmitDelete, 
		register: registerDelete, 
		reset : resetDelete, 
		setError : setErrorDelete, 
		formState: { errors : errorsDelete }, 
		clearErrors: clearErrorsDelete 
	} = useForm();

	const submitDelete = async (data: any) => {
		try{
			clearErrorsDelete();
			modalDelete.current?.setLoadingButton();
			
			await deleteSchema(translate).validate({...data, "mes001TituloConfirmation": deleteSwot?.title},{
				abortEarly: false
			});
			
			await RCCServicesApi.deleteRequest({
				method: "SWOT/Delete",
				urlParameters: {
					id: deleteSwot?.id,
					title: deleteSwot?.title
				}
			});

			addToast({
				color: "green",
				description: translate("Análise SWOT excluída com sucesso" ),
				title: translate("Sucesso")
			});

			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();
			if(onCloseFunction)
				onCloseFunction(deleteSwot?.id);

		}catch(err){

			modalDelete.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorDelete))
				return;

			standardErrorMessages(err, translate("Erro ao excluir análise SWOT"), addToast);
		}
	};
	
	const actionButtonDelete = (swot:{id:number,title:string},onClose?:()=>void) => {
		resetDelete();
		setDeleteSwot(swot);
		setOnCloseFunction(() => onClose);
		modalDelete.current?.openModal();
	};

	const {
		register,
		control,
		formState:{errors},
		setError,
		clearErrors,
		unregister,
		reset,
		handleSubmit
	}= useForm();

	const lovWatch = useWatch({
		control,
		name: "mes001Taa011Id"
	});

	const submitAddOrChange = async(data:any) => {
		try{
			clearErrors();
			modalAddOrChange.current?.setLoadingButton();
			
			const dataForm = data as ISwot;
			dataForm.mes001Id = selectedSwot.mes001Id;

			if(dataForm.permissions && dataForm.permissions.length > 0){
				dataForm.permissions = dataForm.permissions.map((permiss: any) => {
					if(typeof permiss.mes004Efa005Id === "object" && !!permiss.mes004Efa005Id)
						return {
							...permiss,
							mes004Permissao: Number(permiss.mes004Permissao),
							mes004Efa005Id: permiss.mes004Efa005Id.value
						};
					if(typeof permiss.mes004Efa004Id === "object" && !!permiss.mes004Efa004Id)
						return {
							...permiss,
							mes004Permissao: Number(permiss.mes004Permissao),
							mes004Efa004Id: permiss.mes004Efa004Id.value
						};
					return permiss;
				});

				// verifica se tem permissão de Visializar e Editar
				if(!dataForm.permissions.filter((x) => x.mes004Permissao.toString() === simplePermissionType.VIEW_AND_EDIT).length){
					modalAddOrChange.current?.resetLoadingButton();
					addToast({
						color: "yellow",
						description: translate("Ao menos uma permissão deve ser (Visualizar e Editar)"),
						title: translate("Atenção")
					});
					return;
				}
			}
			await AddOrChangeSchema(translate).validate({...dataForm},{
				abortEarly: false
			});
			
			let response;
			if(isAdd){
				response = await RCCServicesApi.postRequest({
					method: "SWOT/Insert",
					body: {
						...dataForm	
					}
				});

			}else if(isCopy){
				response = await RCCServicesApi.postRequest({
					method: "SWOT/Copy",
					body: {
						...dataForm,
						currentLanguage
					}
				});
			}else{
				response = await RCCServicesApi.putRequest({
					method: "SWOT/Update",
					body: {
						...dataForm
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd||isCopy ? translate("Análise SWOT adicionada com sucesso") : translate("Análise SWOT alterada com sucesso"),
				title: translate("Sucesso")
			});

			if(onCloseFunction)
				onCloseFunction(response.data as ISwot);

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();

		}catch(err:any){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setError))
				return;

			standardErrorMessages(err, isAdd||isCopy ? translate("Erro ao adicionar análise SWOT") : translate("Erro ao alterar análise SWOT"), addToast);
		}
	};

	const requestSwot = async(id:number) => {
		const dataController = await RCCServicesApi.getRequest({
			method: "SWOT/Select",
			urlParameters: {
				id
			}
		});

		setSelectedSwot(dataController.data);
	};

	const actionButtonAdd = async(onClose?:(swot?:ISwot)=>void) => {
		reset();
		setIsAdd(true);
		setIsCopy(false);
		const initialPermission =
			[{
				mes004Id:0,
				mes004Efa005Id:user.efa005Id,
				efa005Nome:user.efa005Nome,
				mes004Permissao:simplePermissionType.VIEW_AND_EDIT
			}] as ISwotPermission[];
		setSelectedSwot({permissions:initialPermission} as ISwot);
		setModalTitle(translate("Adicionar análise SWOT"));
		setOnCloseFunction(() => onClose);
		modalAddOrChange.current?.openModal();
	};

	const actionButtonChange = async(id:number,duplicate:boolean,onClose?:(swot?:ISwot)=>void) => {
		try {
			setLoading(true);
			reset();
			setIsCopy(duplicate);
			setIsAdd(false);
			if(duplicate){
				setModalTitle(translate("Copiar análise SWOT"));
			}else{
				setModalTitle(translate("Alterar análise SWOT"));
			}
			await requestSwot(id);
			setOnCloseFunction(() => onClose);
			setLoading(false);
			modalAddOrChange.current?.openModal();
		} catch (error) {
			setLoading(false);
			standardErrorMessages(error, translate("Erro ao buscar análise SWOT"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		actionButtonDelete,
		actionButtonChange,
		actionButtonAdd
	}));

	const PermissionsComponent = useMemo(() => (
		<>
			<Permissions
				register={register}
				unregister={unregister}
				control={control}
				error={errors}
				clearErrors={clearErrors}
				permissionsNames= {{
					nameObject: "permissions",
					nameOfID: "mes004Id",
					nameOfSelectUser: "mes004Efa005Id",
					nameOfSelectGroup: "mes004Efa004Id",
					nameOfSelectPermission: "mes004Permissao",
					nameOfSelectGroupDescription:"efa004Descricao",
					nameOfSelectUserDescription:"efa005Nome",
					defaultValue:selectedSwot.permissions || []
				}}
			/>
		</>
	), [selectedSwot.permissions, errors.permissions]);

	useEffect(() => {
		if (stringToNumber(lovWatch) <= 0)
			lovRef.current?.clearValue();
	}, [lovWatch]);

	const iconModal = () => {
		if(isAdd) return "plus";
		if(isCopy) return "copy";
		return "pencil";
	};

	return (
		<>
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão da análise SWOT")}
				description= {`${translate("Deseja realmente excluir a análise")}: ${deleteSwot?.title}?`}
				onSubmit = {handleSubmitDelete(submitDelete)}
				height="fit-content"
			>
				<RCCGrid
					container
					item
					xs={10}
					styleInline="padding-bottom:50px;"
				>
					<RCCInput 
						register={registerDelete}
						name="mes001Titulo"
						label={translate("Digite o nome da análise para excluí-lo")}
						maxLength={60}
						error={errorsDelete.mes001Titulo}
						required
					/>
				</RCCGrid>
			</DeleteModal>

			{/* Modal ADD/CHANGE */}
			<Modal
				type={isCopy ? ITypeModal.COPY : undefined}
				ref={modalAddOrChange}
				header={{
					title: modalTitle,
					subtitle:!isAdd && !isCopy ? `SWOT:${selectedSwot.mes001Titulo}`:"",
					icon: {
						name: iconModal()
					}
				}}
				onSubmit={handleSubmit(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<Tabs
					name="swotCrudTab"
					tabInit={0}
					options={
						[
							{
								label: translate("Dados"),
								content: 
								<RCCGrid 
									container 
									item 
									justifyContent="center">
									<RCCGrid
										container
										item
										xs={12}
										sm={10}
										alignItems="center"
										justifyContent="space-between"
									>
										{!isCopy && 
										<RCCGrid
											container
											item
											justifyContent="flex-end"
										>
											<RCCGrid item>
												<Audit 
													data={selectedSwot} 
													type="pop" 
													position="left"
												/> 
											</RCCGrid>
										</RCCGrid>
										}
									</RCCGrid>
									<RCCGrid
										container
										item
										xs={12}
										sm={10}
									>
										<RCCInput 
											register={register}
											name="mes001Titulo"
											label={translate("Título")}
											maxLength={60}
											defaultValue={isCopy ? `(${translate("CÓPIA")}) ${selectedSwot.mes001Titulo}` : selectedSwot.mes001Titulo}
											error={errors.mes001Titulo}  
											required
										/>
									</RCCGrid>
									<RCCGrid
										container
										item
										xs={12}
										sm={10}
									>
										<DateTimePickerRange
											control={control}
											nameInit = "mes001Dtinicio"
											nameEnd = "mes001Dtfim"
											label = {translate("Data de Validade")}
											labelSeparator={translate("até")}
											type = "datePicker"
											defaultValueInit = {selectedSwot.mes001Dtinicio}
											defaultValueEnd = {selectedSwot.mes001Dtfim}
											error={errors.mes001Dtinicio||errors.mes001Dtfim}
											required
										/>
									</RCCGrid>
									<RCCGrid
										container
										item
										xs={12}
										sm={10}
									>
										<LovDimensions 
											name="mes001Taa011Id" 
											label={translate("Dimensão padrão")} 
											control={control}
											defaultValue={selectedSwot?.mes001Taa011Id?.toString()}
											required
											extraParametersOnLoad={{
												useConsolidated: false
											}}
											error={errors.mes001Taa011Id}
											ref={lovRef}
										/>
									</RCCGrid>
								</RCCGrid>
							},
							{
								label: translate("Permissões"),
								content: 
								<RCCGrid container item justifyContent="center">
									<RCCGrid container item xs={12} sm={10}>
										{PermissionsComponent}
									</RCCGrid>
								</RCCGrid>
							}
						]
					}
				/>
				
			</Modal>
		</>
	);
};

export default forwardRef(SwotCrudModals);
